import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import apiClient from '../utils/apiClient'; // 👈 AJOUT : Remplacer axios
import Card from "./ui/card";
import Button from "./ui/button";
import Spinner from "./ui/spinner";
import WorkflowIndicator, { WorkflowVisualization } from './WorkflowIndicator'; // 👈 NOUVEAU
import { useConflictResolution } from '../hooks/useConflictResolution'; // 🔍 NOUVEAU - Hook de détection de conflits
import ConflictResolutionModal from './modals/ConflictResolutionModal'; // 🔍 NOUVEAU - Modal de résolution
import './TextToNodeCreator.css';

const LLM_PREVIEW_KEY = 'nodemented_llm_preview';
const LLM_INPUT_KEY = 'nodemented_llm_input';
const LLM_FORMAT_KEY = 'nodemented_llm_format';

const TextToNodeCreator = ({ onNodeCreated, addPendingNode }) => {
  const { t, i18n } = useTranslation();
  const [inputText, setInputText] = useState("");
  const [textFormat, setTextFormat] = useState("free");
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewNodes, setPreviewNodes] = useState([]);
  const [selectedNodeIndex, setSelectedNodeIndex] = useState(0);
  const [editingMetadata, setEditingMetadata] = useState(false);
  const [editableMetadata, setEditableMetadata] = useState("");
  const [existingNodeNames, setExistingNodeNames] = useState([]);
  const [showTutorial, setShowTutorial] = useState(false);
  
  // 🔍 NOUVEAU - Hook de gestion des conflits Vector Echo
  const { conflict, setConflict, isResolving, createNodeWithConflictHandling, resolveConflict } = useConflictResolution();

  const handleWatchTutorial = () => {
    // Ouvrir la vidéo tutoriel - cette fonction peut être étendue pour ouvrir une modale
    window.open('/assets/createNodeText.mp4', '_blank');
  };
  // On mount, restore last LLM analysis if present
  useEffect(() => {
    const cachedPreview = localStorage.getItem(LLM_PREVIEW_KEY);
    const cachedInput = localStorage.getItem(LLM_INPUT_KEY);
    const cachedFormat = localStorage.getItem(LLM_FORMAT_KEY);
    if (cachedPreview) {
      try {
        setPreview(JSON.parse(cachedPreview));
        if (cachedInput) setInputText(cachedInput);
        if (cachedFormat) setTextFormat(cachedFormat);
      } catch (e) {
        localStorage.removeItem(LLM_PREVIEW_KEY);
      }
    }
  }, []);

  // Charger la liste des nœuds existants au montage
  useEffect(() => {
    apiClient.get('/nodes').then((res) => {
      setExistingNodeNames(res.data.map((n) => n.name));
    }).catch((error) => {
      console.error(t('textToNodeCreator.errors.loadingNodes'), error);
      setExistingNodeNames([]);
    });
  }, []);

  // Utility function to detect nodes mentioned in text
  function detectMentionedNodes(text, nodeNames) {
    if (!text) return [];
    return nodeNames.filter((name) =>
      text.toLowerCase().includes(name.toLowerCase())
    );
  }


  // Function to analyze text and generate nodes (preview)
  const previewNode = async () => {
    if (!(inputText || '').trim()) return;
    setLoading(true);
    try {
      const response = await apiClient.post('/preview-node-from-text', {
        text: inputText,
        format: textFormat,
        lang: i18n.language
      });
      let nodes = [];
      if (response.data.node) {
        if (Array.isArray(response.data.node)) {
          nodes = response.data.node;
        } else if (response.data.node.nodes && Array.isArray(response.data.node.nodes)) {
          nodes = response.data.node.nodes;
        } else {
          nodes = [response.data.node];
        }
      }
      setPreviewNodes(nodes);
      setSelectedNodeIndex(0);
      setPreview(nodes[0] || null);
      // Local persistence of LLM response and context
      localStorage.setItem(LLM_PREVIEW_KEY, JSON.stringify(nodes));
      localStorage.setItem(LLM_INPUT_KEY, inputText);
      localStorage.setItem(LLM_FORMAT_KEY, textFormat);

      if (nodes[0] && nodes[0].metadata) {
        setEditableMetadata(
          JSON.stringify(nodes[0].metadata, null, 2)
        );
      }
    } catch (error) {
      // 🔍 GESTION DES CONFLITS (HTTP 409)
      if (error.response && error.response.status === 409) {
        console.log('⚠️ [PREVIEW] Conflit détecté lors de la prévisualisation');
        console.log('📦 [PREVIEW] Données reçues:', error.response.data);
        const conflictData = error.response.data;
        
        // Déclencher la résolution de conflit via le hook
        if (conflictData.conflicts && conflictData.conflicts.length > 0) {
          console.log('⚠️ [PREVIEW] Affichage du modal pour', conflictData.conflicts.length, 'conflit(s)');
          
          // Prendre le premier conflit pour affichage
          const firstConflict = conflictData.conflicts[0];
          console.log('📦 [PREVIEW] Premier conflit:', firstConflict);
          
          // Vérifier la structure
          if (!firstConflict.existingNode || !firstConflict.scores) {
            console.error('❌ [PREVIEW] Structure de conflit invalide:', firstConflict);
            alert('Erreur: Structure de conflit invalide reçue du serveur');
            setLoading(false);
            return;
          }
          
          // Adapter la structure pour le modal
          setConflict({
            hasConflict: true,
            conflictType: firstConflict.conflictType || 'unknown',
            existingNode: firstConflict.existingNode,
            newData: firstConflict.newNode || conflictData.newNode,
            similarity: firstConflict.scores?.global || 0,
            recommendation: firstConflict.recommendation || 'create',
            detailedScores: {
              name: firstConflict.scores?.name || 0,
              type: firstConflict.scores?.type || 0,
              technology: firstConflict.scores?.technology || 0,
              featureTeam: firstConflict.scores?.team || 0,
              description: firstConflict.scores?.semantic || 0
            },
            explanation: `Score de similarité: ${((firstConflict.scores?.global || 0) * 100).toFixed(1)}%`,
            isMultiple: conflictData.conflicts.length > 1,
            currentIndex: 1,
            totalConflicts: conflictData.conflicts.length
          });
        }
        setLoading(false);
        return;
      }
      
      // Autres erreurs
      console.error(t('textToNodeCreator.errors.analysisError'), error);
      alert(t('textToNodeCreator.errors.analysisError', { error: error.message }));
    } finally {
      setLoading(false);
    }
  };

  const createNode = async () => {
    const nodeToCreate = previewNodes[selectedNodeIndex];
    if (!nodeToCreate) return;
    if (!nodeToCreate.name || typeof nodeToCreate.name !== 'string' || !nodeToCreate.name.trim()) {
      alert(t('textToNodeCreator.errors.noNodeName'));
      return;
    }

    setLoading(true);
    try {
      const nodeData = { ...nodeToCreate };

      if (editingMetadata && editableMetadata) {
        try {
          nodeData.metadata = JSON.parse(editableMetadata);
        } catch (e) {
          alert(t('textToNodeCreator.errors.invalidJsonMetadata'));
          setLoading(false);
          return;
        }
      }

      // 🔍 NOUVEAU - Utiliser le système de détection intelligente
      console.log('🔍 [TEXT_TO_NODE] Tentative création avec détection:', nodeData.name);
      
      const result = await createNodeWithConflictHandling(nodeData, '/node-from-text', {
        text: inputText,
        format: textFormat,
        nodes: [nodeData],
        lang: i18n.language
      });

      // Si conflit détecté, arrêter et attendre résolution via modal
      if (result.hasConflict) {
        console.log('⚠️ [TEXT_TO_NODE] Conflit détecté - modal affiché');
        setLoading(false);
        return;
      }

      // Si succès, continuer normalement
      if (result.success) {
        const nodeName = result.node?.name || nodeData.name;
        alert(t('textToNodeCreator.success.nodeCreated', { name: nodeName }));
        resetForm();
        if (onNodeCreated) onNodeCreated();
      }
    } catch (error) {
      console.error(t('textToNodeCreator.errors.creationError'), error);
      alert(t('textToNodeCreator.errors.creationError', { error: error.message }));
    } finally {
      setLoading(false);
    }
  };

  // 🔍 NOUVEAU - Handler pour résoudre les conflits
  const handleConflictResolution = async (resolution) => {
    try {
      console.log('🔄 [TEXT_TO_NODE] Résolution du conflit:', resolution.action);
      
      const result = await resolveConflict(resolution);
      
      if (result.success) {
        const nodeName = result.node?.name || resolution.newData?.name;
        const message = resolution.action === 'enrich'
          ? t('textToNodeCreator.success.nodeEnriched', { name: nodeName })
          : t('textToNodeCreator.success.nodeCreated', { name: nodeName });
        
        alert(message);
        resetForm();
        if (onNodeCreated) onNodeCreated();
      }
    } catch (error) {
      console.error('❌ [TEXT_TO_NODE] Erreur résolution:', error);
      alert(t('textToNodeCreator.errors.resolutionError', { error: error.message }));
    }
  };

  const createAllNodes = async () => {
    if (previewNodes.length === 0) return;
    
    const invalidNodes = previewNodes.filter(node => 
      !node.name || typeof node.name !== 'string' || !node.name.trim()
    );
    
    if (invalidNodes.length > 0) {
      alert(t('textToNodeCreator.errors.invalidNodes', { count: invalidNodes.length }));
      return;
    }

    if (!confirm(t('textToNodeCreator.confirmations.createAllNodes', { count: previewNodes.length }))) {
      return;
    }

    setLoading(true);
    try {
      // 🔍 Note : La détection batch est déjà gérée côté backend dans /node-from-text
      // Si un conflit est détecté, le backend retourne 409 avec tous les conflits
      const response = await apiClient.post('/node-from-text', {
        text: inputText,
        format: textFormat,
        nodes: previewNodes,
        lang: i18n.language
      });

      // Le backend retourne { message: "...", nodes: [...] }
      const createdCount = response.data.nodes ? response.data.nodes.length : previewNodes.length;
      alert(t('textToNodeCreator.success.nodesCreated', { count: createdCount }));
      resetForm();
      if (onNodeCreated) onNodeCreated();
    } catch (error) {
      // Si erreur 409, afficher les conflits détectés
      if (error.response?.status === 409 && error.response?.data?.conflicts) {
        const conflicts = error.response.data.conflicts;
        alert(t('textToNodeCreator.errors.conflictsDetected', { 
          count: conflicts.length,
          total: error.response.data.totalNodes 
        }));
        // TODO: Gérer l'affichage multiple des conflits (pour l'instant on affiche juste une alerte)
      } else {
        console.error(t('textToNodeCreator.errors.creationError'), error);
        alert(t('textToNodeCreator.errors.creationError') + ": " + error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setInputText("");
    setPreview(null);
    setPreviewNodes([]);
    setSelectedNodeIndex(0);
    setEditingMetadata(false);
    setEditableMetadata("");
    // Nettoyer le cache local
    localStorage.removeItem(LLM_PREVIEW_KEY);
    localStorage.removeItem(LLM_INPUT_KEY);
    localStorage.removeItem(LLM_FORMAT_KEY);
  };

  const handlePendingFromPreview = () => {
    // Add selected node (or unique preview) to "being modeled" list without modifying UI
    let nodeToAdd = null;
    if (previewNodes.length > 0) {
      nodeToAdd = previewNodes[selectedNodeIndex];
    } else if (preview) {
      nodeToAdd = preview;
    }
    if (!nodeToAdd) return;
    addPendingNode({
      ...nodeToAdd,
      author: "PO/QA",
      createdAt: new Date().toISOString(),
      status: nodeToAdd.status || "Brouillon",
      isActualNode: true,  // 👈 NOUVEAU : Flag explicite pour identifier les vrais nœuds
      suggestedLinks: nodeToAdd.relationships
        ? nodeToAdd.relationships.map(rel => ({
            type: rel.relationshipType,
            targetNodeName: rel.targetNodeName
          }))
        : [],
    });
    resetForm();
  };

  const handlePendingAllNodes = () => {
    if (previewNodes.length === 0) return;
    
    if (!confirm(t('textToNodeCreator.confirmations.addAllToPending', { count: previewNodes.length }))) {
      return;
    }

    previewNodes.forEach(node => {
      addPendingNode({
        ...node,
        author: t('textToNodeCreator.author'),
        createdAt: new Date().toISOString(),
        status: node.status || t('textToNodeCreator.status.draft'),
        isActualNode: true,  // 👈 NOUVEAU : Flag explicite pour identifier les vrais nœuds
        suggestedLinks: node.relationships
          ? node.relationships.map(rel => ({
              type: rel.relationshipType,
              targetNodeName: rel.targetNodeName
            }))
          : [],
      });
    });
    resetForm();
  };

  // Smart detection for preview (on selected node)
  const currentPreview = previewNodes[selectedNodeIndex] || preview;
  const mentionedExisting = (currentPreview?.relationships || [])
    .filter((r) => r.exists)
    .map((r) => r.targetNodeName);

  const mentionedNew = (currentPreview?.relationships || [])
    .filter((r) => !r.exists)
    .map((r) => r.targetNodeName);

  // 🎯 Function to get icon based on format
  const getFormatIcon = () => {
    return textFormat === "gherkin" ? "🥒" : "✍️";
  };

  // 🎯 Suggestions de formats populaires — chaque item porte son label + la clé d'exemple à injecter
  const quickSuggestions = {
    free: [
      { label: t('textToNodeCreator.suggestions.describeApi'),    exampleKey: 'freeLogin' },
      { label: t('textToNodeCreator.suggestions.explainComponent'), exampleKey: 'freeComponent' },
      { label: t('textToNodeCreator.suggestions.documentDatabase'), exampleKey: 'freeDatabase' }
    ],
    gherkin: [
      { label: t('textToNodeCreator.suggestions.authScenario'),   exampleKey: 'gherkinAuth' },
      { label: t('textToNodeCreator.suggestions.formValidation'), exampleKey: 'gherkinFormValidation' },
      { label: t('textToNodeCreator.suggestions.orderWorkflow'),  exampleKey: 'gherkinOrder' }
    ]
  };

  return (
    <div className="modern-text-creator-container">
      {/* Header moderne */}
      <div className="text-creator-header">
        <div className="creator-summary-card">
          <div className="creator-indicator">
            <span className="creator-icon">{getFormatIcon()}</span>
            <div className="creator-info">
              <h3 className="creator-title">✨ {t('textToNodeCreator.title')}</h3>
              <p className="creator-description">
                {t('textToNodeCreator.description')}
              </p>
            </div>
          </div>
          
          <div className="format-status">
            <div className="format-indicator">
              <span className="format-dot"></span>
              <span className="format-text">
                {t('textToNodeCreator.format.formatStatus', { 
                  format: textFormat === "gherkin" ? t('textToNodeCreator.format.gherkin') : t('textToNodeCreator.format.free')
                })}
              </span>
            </div>
            <button 
              className="tutorial-btn" 
              onClick={handleWatchTutorial}
              title={t('textToNodeCreator.tutorial.watchVideo', 'Watch video tutorial')}
            >
              <span className="tutorial-icon">📹</span>
              <span className="tutorial-text">{t('textToNodeCreator.tutorial.title')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Corps principal */}
      <div className="text-creator-body">
        
        {/* Sélecteur de format modernisé */}
        <div className="format-controls-section">
          <div className="format-selector">
            <div className="format-tabs">
              <button
                type="button"
                className={`format-tab ${textFormat === "free" ? "active" : ""}`}
                onClick={() => setTextFormat("free")}
              >
                <span className="tab-icon">✍️</span>
                <span className="tab-label">{t('textToNodeCreator.format.free')}</span>
                <span className="tab-description">{t('textToNodeCreator.format.freeDescription')}</span>
              </button>
              <button
                type="button"
                className={`format-tab ${textFormat === "gherkin" ? "active" : ""}`}
                onClick={() => setTextFormat("gherkin")}
              >
                <span className="tab-icon">🥒</span>
                <span className="tab-label">{t('textToNodeCreator.format.gherkin')}</span>
                <span className="tab-description">{t('textToNodeCreator.format.gherkinDescription')}</span>
              </button>
            </div>
          </div>

          {/* Suggestions rapides */}
          <div className="format-suggestions">
            <span className="suggestions-label">{t('textToNodeCreator.suggestions.label', { 
              format: textFormat === "gherkin" ? t('textToNodeCreator.format.gherkin') : t('textToNodeCreator.format.free')
            })}</span>
            <div className="suggestion-chips">
              {quickSuggestions[textFormat].map((suggestion, idx) => (
                <button
                  key={idx}
                  type="button"
                  className="suggestion-chip"
                  onClick={() => setInputText(t(`textToNodeCreator.examples.${suggestion.exampleKey}`))}
                >
                  {suggestion.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Zone de saisie améliorée */}
        <div className="text-input-section">
          <div className="input-container">
            <div className="input-header">
              <span className="input-icon">{getFormatIcon()}</span>
              <span className="input-label">
                {textFormat === "gherkin" ? t('textToNodeCreator.input.gherkinLabel') : t('textToNodeCreator.input.freeLabel')}
              </span>
              <div className="char-counter">
                {(inputText || "").length} {t('textToNodeCreator.input.characters')}
              </div>
            </div>
            
            <textarea
              rows={8}
              className="modern-text-input"
              value={inputText}
              onChange={(e) => {
                const maxLength = 2500; // Set the character limit
                if (e.target.value.length <= maxLength) {
                  setInputText(e.target.value);
                }
              }}
              placeholder={
                textFormat === "gherkin"
                  ? t('textToNodeCreator.placeholders.gherkinExample')
                  : t('textToNodeCreator.placeholders.freeDescription')
              }
              disabled={loading}
            />
            <div className="char-counter">
              {inputText.length} / 2500 {t('textToNodeCreator.input.characters')}
            </div>
            
            <div className="input-footer">
              <div className="input-tips">
                💡 {textFormat === "gherkin" 
                  ? t('textToNodeCreator.tips.gherkin')
                  : t('textToNodeCreator.tips.free')}
              </div>
              
              <button
                className="analyze-btn"
                onClick={previewNode}
                disabled={loading || !(inputText || "").trim()}
              >
                {loading && !preview ? (
                  <>
                    <span className="btn-spinner"></span>
                    <span>{t('textToNodeCreator.buttons.analyzing')}</span>
                  </>
                ) : (
                  <>
                    <span className="btn-icon">🔍</span>
                    <span>{t('textToNodeCreator.buttons.analyzeText')}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Aperçu intelligent des nœuds mentionnés */}
        {(mentionedExisting.length > 0 || mentionedNew.length > 0) && (
          <div className="mentioned-nodes-section">
            <h4 className="section-title">{t('textToNodeCreator.sections.detectedNodes')}</h4>
            <div className="mentioned-nodes-grid">
              {mentionedExisting.map((name) => (
                <div key={name} className="mentioned-node existing">
                  <span className="node-icon">✅</span>
                  <span className="node-name">{name}</span>
                  <span className="node-status">{t('textToNodeCreator.status.alreadyExists')}</span>
                </div>
              ))}
              {mentionedNew.map((name) => (
                <div key={name} className="mentioned-node new">
                  <span className="node-icon">⚠️</span>
                  <span className="node-name">{name}</span>
                  <span className="node-status">{t('textToNodeCreator.status.willBeCreated')}</span>
                </div>
              ))}
            </div>
          </div>
        )}


        {/* Aperçu des nœuds générés avec navigation par onglets */}
        {previewNodes.length > 0 && (
          <div className="node-preview-section">
            <h4 className="section-title">{t('textToNodeCreator.sections.generatedPreview', { count: previewNodes.length })}</h4>
            {/* Tabs pour chaque nœud */}
            <div className="node-tabs">
              {previewNodes.map((n, idx) => (
                <button
                  key={idx}
                  className={`node-tab-btn${selectedNodeIndex === idx ? ' selected' : ''}`}
                  onClick={() => {
                    setSelectedNodeIndex(idx);
                    setPreview(previewNodes[idx]);
                    if (previewNodes[idx].metadata) {
                      setEditableMetadata(JSON.stringify(previewNodes[idx].metadata, null, 2));
                    }
                  }}
                >
                  {n.name || t('textToNodeCreator.defaultNodeName', { index: idx + 1 })}
                </button>
              ))}
            </div>

            {/* Résumé des relations détectées entre tous les nœuds (lorsque plusieurs nœuds sont générés) */}
            {previewNodes.length > 1 && (
              <div className="all-relationships-summary">
                <h4>{t('textToNodeCreator.sections.detectedRelationships')}</h4>
                <ul>
                  {previewNodes.flatMap((node) =>
                    (node.relationships || []).map((rel, idx) => (
                      <li key={node.name + rel.targetNodeName + idx}>
                        <strong>{node.name}</strong> — <em>{rel.relationshipType}</em> → <strong>{rel.targetNodeName}</strong>
                        {rel.exists ? t('textToNodeCreator.status.existing') : t('textToNodeCreator.status.new')}
                      </li>
                    ))
                  )}
                </ul>
              </div>
            )}

            {/* Détail du nœud sélectionné */}
            <div className="preview-container">
              {/* En-tête du preview */}
              <div className="preview-header">
                <div className="preview-badge">
                  <span className="preview-icon">🎯</span>
                  <span className="preview-label">{t('textToNodeCreator.readyToCreate')}</span>
                </div>
                <div className="preview-type">
                  <span className="type-badge">{currentPreview.type || t('textToNodeCreator.unspecified')}</span>
                </div>
              </div>

              {/* Informations principales */}
              <div className="preview-main-info">
                <div className="info-grid">
                  <div className="info-card">
                    <div className="info-header">
                      <span className="info-icon">🏷️</span>
                      <span className="info-label">{t('titles.name')}</span>
                    </div>
                    <div className="info-value">{currentPreview.name}</div>
                  </div>
                  
                  <div className="info-card">
                    <div className="info-header">
                      <span className="info-icon">⚙️</span>
                      <span className="info-label">{t('titles.technology')}</span>
                    </div>
                    <div className="info-value">{currentPreview.technology || t('textToNodeCreator.unspecified')}</div>
                  </div>
                  
                  <div className="info-card">
                    <div className="info-header">
                      <span className="info-icon">📊</span>
                      <span className="info-label">{t('titles.status')}</span>
                    </div>
                    <div className="info-value">{currentPreview.status || t('textToNodeCreator.status.new')}</div>
                  </div>

                  <div className="info-card">
                    <div className="info-header">
                      <span className="info-icon">👥</span>
                      <span className="info-label">{t('titlesFeatureTeam')}</span>
                    </div>
                    <input
                      type="text"
                      className="info-input"
                      value={currentPreview.featureTeam || ""}
                      onChange={(e) => {
                        const updatedNodes = [...previewNodes];
                        updatedNodes[selectedNodeIndex].featureTeam = e.target.value;
                        setPreviewNodes(updatedNodes);
                      }}
                      placeholder={t('textToNodeCreator.placeholders.featureTeam') || "e.g., Frontend Team, Backend Squad..."}
                    />
                  </div>
                </div>

                {/* Description */}
                <div className="description-card">
                  <div className="description-header">
                    <span className="description-icon">📝</span>
                    <span className="description-label">{t('titles.description')}</span>
                  </div>
                <div className="description-content">
                  {typeof currentPreview.description === 'string' ? (
                    currentPreview.description
                  ) : currentPreview.description && typeof currentPreview.description === 'object' ? (
                    currentPreview.description.scenario && currentPreview.description.given && currentPreview.description.when && currentPreview.description.then ? (
                      <div style={{fontFamily: 'monospace', fontSize: '0.97em', background: '#f8f8fa', borderRadius: 8, padding: 8, margin: 0}}>
                        <div><strong>{t('textToNodeCreator.gherkinLabels.scenario')}:</strong> {currentPreview.description.scenario}</div>
                        <div><strong>{t('textToNodeCreator.gherkinLabels.given')}:</strong> {Array.isArray(currentPreview.description.given) ? currentPreview.description.given.map((g, i) => <div key={i}>- {g}</div>) : currentPreview.description.given}</div>
                        <div><strong>{t('textToNodeCreator.gherkinLabels.when')}:</strong> {Array.isArray(currentPreview.description.when) ? currentPreview.description.when.map((w, i) => <div key={i}>- {w}</div>) : currentPreview.description.when}</div>
                        <div><strong>{t('textToNodeCreator.gherkinLabels.then')}:</strong> {Array.isArray(currentPreview.description.then) ? currentPreview.description.then.map((t, i) => <div key={i}>- {t}</div>) : currentPreview.description.then}</div>
                      </div>
                    ) : (
                      <pre style={{whiteSpace: 'pre-wrap', fontSize: '0.95em', background: '#f8f8fa', borderRadius: 8, padding: 8, margin: 0}}>
                        {JSON.stringify(currentPreview.description, null, 2)}
                      </pre>
                    )
                  ) : null}
                </div>
                </div>
              </div>

              {/* Critères d'acceptation */}
              {currentPreview.acceptanceCriteria && (
                <div className="acceptance-section">
                  <div className="acceptance-header">
                    <span className="acceptance-icon">✅</span>
                    <span className="acceptance-label">{t('textToNodeCreator.sections.acceptanceCriteria')}</span>
                  </div>
                  {Array.isArray(currentPreview.acceptanceCriteria) ? (
                    <ul className="acceptance-list">
                      {currentPreview.acceptanceCriteria.map((crit, i) => (
                        <li key={i}>
                          {typeof crit === 'string' ? crit : <pre style={{whiteSpace: 'pre-wrap', fontSize: '0.95em', background: '#070707ff', borderRadius: 8, padding: 8, margin: 0}}>{JSON.stringify(crit, null, 2)}</pre>}
                        </li>
                      ))}
                    </ul>
                  ) : typeof currentPreview.acceptanceCriteria === 'object' ? (
                    <pre style={{whiteSpace: 'pre-wrap', fontSize: '0.95em', background: '#0c0c0cff', borderRadius: 8, padding: 8, margin: 0}}>
                      {JSON.stringify(currentPreview.acceptanceCriteria, null, 2)}
                    </pre>
                  ) : null}
                </div>
              )}

              {/* Workflow détecté */}
              {currentPreview.workflow && (
                <div className="workflow-section">
                  <WorkflowIndicator workflow={currentPreview.workflow} />
                </div>
              )}

              {/* Métadonnées */}
              {currentPreview.metadata && Object.keys(currentPreview.metadata).length > 0 && (
                <div className="metadata-section">
                  <div className="metadata-header">
                    <span className="metadata-icon">🔧</span>
                    <span className="metadata-label">{t('textToNodeCreator.sections.detectedMetadata')}</span>
                    <button
                      className="metadata-toggle-btn"
                      onClick={() => setEditingMetadata(!editingMetadata)}
                    >
                      {editingMetadata ? t('textToNodeCreator.buttons.preview') : t('textToNodeCreator.buttons.edit')}
                    </button>
                  </div>

                  {editingMetadata ? (
                    <div className="metadata-editor-container">
                      <textarea
                        className="metadata-editor"
                        value={editableMetadata}
                        onChange={(e) => setEditableMetadata(e.target.value)}
                        placeholder="Saisir les métadonnées en JSON..."
                      />
                    </div>
                  ) : (
                    <div className="metadata-display">
                      <pre className="metadata-json">
                        {JSON.stringify(currentPreview.metadata, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              )}

              {/* Schéma d'endpoint */}
              {currentPreview.endpointSchema && Object.keys(currentPreview.endpointSchema).length > 0 && (
                <div className="endpoint-schema-section">
                  <div className="schema-header">
                    <span className="schema-icon">🌐</span>
                    <span className="schema-label">{t('textToNodeCreator.sections.endpointSchema')}</span>
                  </div>
                  <div className="schema-display">
                    <pre className="schema-json">
                      {JSON.stringify(currentPreview.endpointSchema, null, 2)}
                    </pre>
                  </div>
                </div>
              )}

              {/* Relations détectées */}
              {currentPreview.relationships && currentPreview.relationships.length > 0 && (
                <div className="relationships-section">
                  <div className="relationships-header">
                    <span className="relationships-icon">🔗</span>
                    <span className="relationships-label">{t('textToNodeCreator.sections.detectedRelations')}</span>
                    <span className="relationships-count">{currentPreview.relationships.length}</span>
                  </div>
                  
                  <div className="relationships-grid">
                    {currentPreview.relationships.map((rel, index) => (
                      <div key={index} className="relationship-card">
                        <div className="relationship-flow">
                          <span className="source-node">{currentPreview.name}</span>
                          <span className="relationship-arrow">→</span>
                          <span className="relationship-type">{rel.relationshipType}</span>
                          <span className="relationship-arrow">→</span>
                          <span className="target-node">{rel.targetNodeName}</span>
                        </div>
                        <div className="relationship-status">
                          {rel.exists ? (
                            <span className="status-exists">✅ {t('textToNodeCreator.status.existingNode')}</span>
                          ) : (
                            <span className="status-new">⚠️ {t('textToNodeCreator.newNode')}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="preview-actions">
                <button
                  className="action-btn primary"
                  onClick={createNode}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="btn-spinner"></span>
                      <span>{t('textToNodeCreator.buttons.creating')}</span>
                    </>
                  ) : (
                    <>
                      <span className="btn-icon">💾</span>
                      <span>{t('textToNodeCreator.createThisNode')}</span>
                    </>
                  )}
                </button>

                {previewNodes.length > 1 && (
                  <button
                    className="action-btn primary-alt"
                    onClick={createAllNodes}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="btn-spinner"></span>
                        <span>{t('textToNodeCreator.buttons.creating')}</span>
                      </>
                    ) : (
                      <>
                        <span className="btn-icon">🚀</span>
                        <span>{t('textToNodeCreator.createAllNodes', { count: previewNodes.length })}</span>
                      </>
                    )}
                  </button>
                )}
                
                <button
                  className="action-btn secondary"
                  onClick={handlePendingFromPreview}
                  disabled={loading}
                >
                  <span className="btn-icon">🧱</span>
                  <span>{t('textToNodeCreator.buttons.addToPending')}</span>
                </button>

                {previewNodes.length > 1 && (
                  <button
                    className="action-btn secondary-alt"
                    onClick={handlePendingAllNodes}
                    disabled={loading}
                  >
                    <span className="btn-icon">📋</span>
                    <span>{t('textToNodeCreator.buttons.addAllToPending', { count: previewNodes.length })}</span>
                  </button>
                )}
                
                <button
                  className="action-btn tertiary"
                  onClick={resetForm}
                  disabled={loading}
                >
                  <span className="btn-icon">↩️</span>
                  <span>{t('textToNodeCreator.buttons.restart')}</span>
                </button>
              </div>
            </div>
          </div>
        )}

      </div>

      {/* 🔍 NOUVEAU - Modal de résolution de conflits */}
      {conflict && (
        <ConflictResolutionModal
          conflict={conflict}
          onResolve={handleConflictResolution}
          onCancel={() => setConflict(null)}
          isResolving={isResolving}
        />
      )}
    </div>
  );
};

export default TextToNodeCreator;
